import { filter, take } from 'rxjs';

import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { AuthFacade, RouteFacade, UserPermission } from '@mitsubishi/core';
import { ExpiringMarkup, PriceListService } from '@mitsubishi/pricing-list/data-access';
import { DialogService } from '@mitsubishi/ui/dialog';

import { ExpiringMarkupBlockingDialogComponent } from './components/expiring-markup-blocking-dialog/expiring-markup-blocking-dialog.component';
import { ExpiringMarkupInfoDialogComponent } from './components/expiring-markup-info-dialog/expiring-markup-info-dialog.component';
import { getBlockingExpiringMarkups } from './expiring-markup.utils';

const HOME_ROUTE = '/';

@Injectable({ providedIn: 'root' })
export class ExpiringMarkupAlertService {
  readonly #priceListService = inject(PriceListService);
  readonly #dialogService = inject(DialogService);
  readonly #routeFacade = inject(RouteFacade);
  readonly #authFacade = inject(AuthFacade);
  readonly #router = inject(Router);

  // Populated when a non-blocking alert should be rendered inline (home page).
  // Empty when a dialog is used instead or there is nothing to show.
  readonly nonBlockingMarkups = signal<ExpiringMarkup[]>([]);

  checkAndShow(): void {
    // Permissions land in the store on ngxsAfterBootstrap, which fires after
    // AppComponent init — so wait for the user profile before reading them.
    this.#authFacade.userInfo$.pipe(filter(Boolean), take(1)).subscribe(() => {
      if (this.#routeFacade.data['isPublicRoute'] === true) {
        return;
      }
      if (!this.#authFacade.hasPermission(UserPermission.ManageMarkup)) {
        return;
      }

      this.#priceListService.getExpiringMarkups().subscribe(list => this.#showAlert(list));
    });
  }

  dismissBannerAlert(): void {
    this.#priceListService.markMarkupAlertRead().subscribe();
    this.nonBlockingMarkups.set([]);
  }

  #showAlert(list: ExpiringMarkup[]): void {
    const blocking = getBlockingExpiringMarkups(list);

    if (blocking.length) {
      this.#dialogService.openNotClosableDialog(ExpiringMarkupBlockingDialogComponent, 'xl', { rows: blocking });
      return;
    }

    if (!list.length) {
      return;
    }

    if (this.#router.url.split(/[?#]/)[0] === HOME_ROUTE) {
      // Home renders an inline banner instead; alert-read fires on banner dismiss.
      this.nonBlockingMarkups.set(list);
      return;
    }

    this.#priceListService.markMarkupAlertRead().subscribe();
    this.#dialogService.openDialog(ExpiringMarkupInfoDialogComponent, 'md', { count: list.length });
  }
}
